import { ChevronUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { SimpleTooltip } from '@/components/ui/simple-tooltip';
import { cn } from '@/lib/utils';
import {
  SummaryCardsCarousel,
  SummaryCardSlot,
} from '@/modules/common/components/molecules/summary-cards-carousel';

import { ApprovalsSummary } from '../../types/time-tracking-approvals';
import { ApprovedForBillCard, ApprovedForBillChip } from './cards/approved-for-bill-card';
import { ApprovedForInvoiceCard, ApprovedForInvoiceChip } from './cards/approved-for-invoice-card';
import { hasIncomeToShow, IncomeCard, IncomeChip } from './cards/income-card';
import { TrackedVsBookedCard, TrackedVsBookedChip } from './cards/tracked-vs-booked-card';
import { SummaryCardSkeleton } from './shared/summary-card-skeleton';
import { SummaryChipSkeleton } from './shared/summary-chip';

// Tracked-vs-Booked + the two approval cards are always shown; the Income card only for
// users with the billing permission — mirror that so the skeleton count matches.
const BASE_CARD_COUNT = 3;

/** Id linking the toggle button (aria-controls) to the collapsible cards region. */
export const SUMMARY_CARDS_REGION_ID = 'approvals-summary-cards';

interface ApprovalsSummarySectionProps {
  summary: ApprovalsSummary | undefined;
  isLoading: boolean;
  /** User-controlled collapsed state — cards give way to a compact chip strip. */
  collapsed: boolean;
  /** Income card is only rendered for users with the billing permission. */
  showIncomeCard: boolean;
}

/**
 * The approvals top section: a snap carousel of stat cards, each owning its own maths, copy
 * and tooltip in `./cards/*`. This file only composes and lays them out.
 *
 * When collapsed the cards yield to a one-line chip strip, so the entries table — the main
 * content — gets the space. Collapsed state is user-controlled and persisted (see the page's
 * localStorage wiring); the toggle button lives in the page header, see
 * `ApprovalsSummaryToggleButton`.
 *
 * Why the two approved values get a card each instead of sharing one: `StatCard` is `h-full`
 * inside a flex carousel, so the tallest card sets the height of EVERY card. A fourth
 * `StatCardRow` on a single card would grow all of them and steal that space from the table on
 * every page load. Four cards that each keep the 2-rows-plus-meter shape are height-neutral.
 */
export const ApprovalsSummarySection = ({
  summary,
  isLoading,
  collapsed,
  showIncomeCard,
}: ApprovalsSummarySectionProps) => {
  const cardCount = BASE_CARD_COUNT + (showIncomeCard ? 1 : 0);
  const showIncome = summary ? hasIncomeToShow(summary, showIncomeCard) : false;

  return (
    <div className="flex flex-col">
      <div
        id={SUMMARY_CARDS_REGION_ID}
        className={cn(
          'grid transition-all duration-300',
          collapsed ? 'grid-rows-[0fr] opacity-0' : 'grid-rows-[1fr] opacity-100'
        )}
        aria-hidden={collapsed}
      >
        <div className="min-h-0 overflow-hidden">
          {isLoading ? (
            <div className="flex gap-4 overflow-hidden pb-1">
              {Array.from({ length: cardCount }).map((_, index) => (
                <SummaryCardSlot key={index} compact>
                  <SummaryCardSkeleton />
                </SummaryCardSlot>
              ))}
            </div>
          ) : summary ? (
            <SummaryCardsCarousel>
              <SummaryCardSlot compact>
                <TrackedVsBookedCard summary={summary} />
              </SummaryCardSlot>

              <SummaryCardSlot compact>
                <ApprovedForInvoiceCard summary={summary} />
              </SummaryCardSlot>

              <SummaryCardSlot compact>
                <ApprovedForBillCard summary={summary} />
              </SummaryCardSlot>

              {showIncome && (
                <SummaryCardSlot compact>
                  <IncomeCard summary={summary} />
                </SummaryCardSlot>
              )}
            </SummaryCardsCarousel>
          ) : null}
        </div>
      </div>

      {collapsed &&
        (isLoading ? (
          <div className="flex gap-2 overflow-hidden">
            {Array.from({ length: cardCount }).map((_, index) => (
              <SummaryChipSkeleton key={index} />
            ))}
          </div>
        ) : summary ? (
          <div className="flex gap-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <TrackedVsBookedChip summary={summary} />
            <ApprovedForInvoiceChip summary={summary} />
            <ApprovedForBillChip summary={summary} />
            {showIncome && <IncomeChip summary={summary} />}
          </div>
        ) : null)}
    </div>
  );
};

interface ApprovalsSummaryToggleButtonProps {
  collapsed: boolean;
  onToggleCollapsed: () => void;
}

/**
 * Collapse/expand handle for the summary section, rendered as the page's
 * `secondaryActions` — inside the sticky header (not the scrollable content area, which
 * clips overflow) so it can hang below the header's bottom border without being cut off.
 * The header container has no bottom padding, so its bottom edge (`bottom: 0`) sits right
 * on the shadow border; `-bottom-3` (12px, half the button's 24px height) centers the
 * pill on that line. Requires the header container to be `position: relative`.
 */
export const ApprovalsSummaryToggleButton = ({
  collapsed,
  onToggleCollapsed,
}: ApprovalsSummaryToggleButtonProps) => {
  const { t } = useTranslation('authenticated.timeTracking');

  const toggleLabel = collapsed
    ? t('approvals.summaryCards.toggle.show')
    : t('approvals.summaryCards.toggle.hide');

  return (
    <SimpleTooltip content={toggleLabel}>
      <button
        type="button"
        onClick={onToggleCollapsed}
        aria-expanded={!collapsed}
        aria-controls={SUMMARY_CARDS_REGION_ID}
        aria-label={toggleLabel}
        className={cn(
          'absolute -bottom-3 left-1/2 z-10 flex h-6 w-9 -translate-x-1/2 items-center justify-center',
          'rounded-full border border-border bg-background text-muted-foreground shadow-md',
          'transition-colors hover:bg-muted hover:text-foreground',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
        )}
      >
        <ChevronUp
          aria-hidden
          className={cn('h-4 w-4 transition-transform duration-300', collapsed && 'rotate-180')}
        />
      </button>
    </SimpleTooltip>
  );
};
